import {
  APPROVAL_DECISION_LABELS,
  ApprovalDecisionValue,
  halloCommaSalutation,
  type DecideApprovalRequest,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { useVoice } from '@/platform/voice/use-voice';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useApprovalActionPreview,
  useSubmitApprovalAction,
} from '@/products/helpdesk/features/approvals/use-approvals';
import { ApiRequestError } from '@/lib/api';

export function ApprovalActionPage() {
  const voice = useVoice();
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const preview = useApprovalActionPreview(token);
  const submit = useSubmitApprovalAction(token ?? '');
  const [comment, setComment] = useState('');
  const [done, setDone] = useState<DecideApprovalRequest['decision'] | null>(null);

  const preset = useMemo(() => {
    const raw = params.get('aktion');
    if (raw === 'freigeben') {
      return ApprovalDecisionValue.APPROVED;
    }
    if (raw === 'ablehnen') {
      return ApprovalDecisionValue.REJECTED;
    }
    return null;
  }, [params]);

  async function onSubmit(decision: DecideApprovalRequest['decision']) {
    try {
      await submit.mutateAsync({ decision, comment: comment.trim() || undefined });
      setDone(decision);
      toast.success(
        decision === ApprovalDecisionValue.APPROVED ? 'Freigabe erteilt.' : 'Freigabe abgelehnt.',
      );
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Entscheidung konnte nicht gespeichert werden.',
      );
    }
  }

  if (!token) {
    return <p className="text-muted-foreground">Ungültiger Link.</p>;
  }

  if (preview.isPending) {
    return (
      <p className="flex items-center gap-2 text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Freigabe wird geladen …
      </p>
    );
  }

  const data = preview.data;
  if (!data?.ticketNumber) {
    return (
      <section className="mx-auto max-w-lg space-y-3">
        <h1 className="text-2xl font-bold">Freigabelink ungültig</h1>
        <p className="text-muted-foreground">Der Link ist ungültig oder unbekannt.</p>
      </section>
    );
  }

  if (done) {
    return (
      <section className="mx-auto max-w-lg space-y-4">
        <h1 className="text-2xl font-bold">{APPROVAL_DECISION_LABELS[done]}</h1>
        <p className="text-muted-foreground">
          Ticket #{data.ticketNumber} wurde{' '}
          {done === ApprovalDecisionValue.APPROVED ? 'freigegeben' : 'abgelehnt'}.
        </p>
        <Button asChild>
          <Link to={`/helpdesk/tickets/${data.ticketNumber}`}>Ticket öffnen</Link>
        </Button>
      </section>
    );
  }

  const blocked = !data.tokenValid;
  const reason = data.alreadyUsed
    ? 'Dieser Link wurde bereits verwendet.'
    : data.expired
      ? 'Dieser Link ist abgelaufen.'
      : data.requestStatus && data.requestStatus !== 'PENDING'
        ? 'Diese Freigabe ist bereits abgeschlossen.'
        : 'Dieser Link kann nicht verwendet werden.';

  return (
    <section className="mx-auto max-w-lg space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Ticketfreigabe</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {data.approverDisplayName
            ? `${halloCommaSalutation({ displayName: data.approverDisplayName }, voice)} `
            : ''}
          bitte entscheiden Sie über Ticket #{data.ticketNumber}.
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>
            #{data.ticketNumber} · {data.ticketSubject}
          </CardTitle>
          <CardDescription>
            {data.workflowName}
            {data.stepName ? ` · ${data.stepName}` : ''}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {blocked ? (
            <p className="text-sm">{reason}</p>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="email-approval-comment">Kommentar (optional)</Label>
                <Input
                  id="email-approval-comment"
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  disabled={submit.isPending}
                  onClick={() => void onSubmit(ApprovalDecisionValue.APPROVED)}
                >
                  {submit.isPending && preset !== ApprovalDecisionValue.REJECTED && (
                    <Loader2Icon className="animate-spin" aria-hidden />
                  )}
                  Freigeben
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={submit.isPending}
                  onClick={() => void onSubmit(ApprovalDecisionValue.REJECTED)}
                >
                  Ablehnen
                </Button>
              </div>
            </>
          )}
          <Button asChild variant="outline" size="sm">
            <Link to={`/helpdesk/tickets/${data.ticketNumber}`}>Im Portal öffnen</Link>
          </Button>
        </CardContent>
      </Card>
    </section>
  );
}
